import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';
import CustomerInfoModal from '../components/Customer/CustomerInfoModal';
import CreateFollowUpModal from '../components/Followup/CreateFollowupModal';
import QueryHistoryModal from '../components/Query/QueryHistoryModal';
import AssignmentModal from '../components/Query/AssignmentModal';
import StartQuotationModal from '../components/Quotation/StartQuotationModal';
import { fetchBookingDrafts } from '../api/BookingDraft';
// import ViewQuotationsModal from '../components/Quotation/ViewQuotationsModal';
import CreateQueryModal from '../components/Query/CreateQueryModal';
import CustomerPickerModal from '../components/Customer/CustomerPickerModal';

import { Query } from '../types/Query';
import { Customer } from '../types/Customer';
import { User } from '../types/User';
import { fetchCustomerById } from '../api/Customer';
import { fetchQueries, updateQueryStatus } from '../api/Query';
import { formatDate } from '../utils/date';

import '../css/Pages.css';
import '../css/PackagesPage.css';
import '../css/Kanban.css';

import * as FiIcons from 'react-icons/fi';
const FaEye = FiIcons.FiEye as unknown as React.FC;
const FaPlus = FiIcons.FiPlus as unknown as React.FC;
const FaShare = FiIcons.FiShare as unknown as React.FC;
const FaAssign = FiIcons.FiUserPlus as unknown as React.FC;
const FiRefreshCw = FiIcons.FiRefreshCw as unknown as React.FC;

const QueriesPage = () => {
  const hasFetched = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // const [showViewFollowUpsModal, setShowViewFollowUpsModal] = useState(false);
  // const [showViewQuotationsModal, setShowViewQuotationsModal] = useState(false);
  const [showQueryHistory, setShowQueryHistory] = useState(false);
  const [showCreateFollowUpModal, setShowCreateFollowUpModal] = useState(false);
  const [showShareQuotationModal, setShowShareQuotationModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  const [queryId, setQueryId] = useState<number>(0);
  const [queries, setQueries] = useState<Query[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [newQueryCustomerId, setNewQueryCustomerId] = useState<number | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const [typeFilter, setTypeFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const navigate = useNavigate();

  const load = async (customerId?: number) => {
    try {
      setLoading(true);
      const data = await fetchQueries(customerId || 0);
      setQueries(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const customerId = parseInt(searchParams.get('customerId') || '0');
    load(customerId);
  }, [searchParams]);

  const closeAllModals = () => {
    setShowCreateFollowUpModal(false);
  };

  const openCreateFollowUpModal = (queryId?: number) => {
    setQueryId(queryId || 0);
    setShowCreateFollowUpModal(true);
  };

  const openShareQuotationModal = async (qid?: number) => {
    const id = qid || 0;
    setQueryId(id);
    try {
      if (id) {
        const drafts = await fetchBookingDrafts(id);
        const list = Array.isArray(drafts) ? drafts : [];
        const filtered = list.filter((d: any) => Number(d.query_id || d.queryId || 0) === Number(id));
        if (filtered.length > 0) {
          const sorted = filtered.slice().sort((a: any, b: any) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
          const d = sorted[0];
          let pkg = (d as any).provider_ref;
          if (typeof pkg === 'string') { try { pkg = JSON.parse(pkg); } catch { /* ignore */ } }
          if (pkg) {
            navigate('/package/builder', { state: { mode: 'quotation', queryId: id, packageFromDraft: pkg } });
            return;
          }
        }
      }
    } catch {
      // fall back to modal
    }
    setShowShareQuotationModal(true);
  };

  const viewFollowUps = (queryId?: number) => {
    setQueryId(queryId || 0);
    setShowQueryHistory(true);
  };


  const openCustomerModal = async (customerId?: number) => {
    if (!customerId) return;
    try {
      const customer = await fetchCustomerById(customerId);
      setSelectedCustomer(customer);
    } catch (err) {
    }
  };

  const openAssignmentModal = (queryId?: number) => {
    setQueryId(queryId || 0);
    setShowAssignmentModal(true);
  };

  const handleAssign = async (queryId: number, userId: number) => {
    setQueries(prev =>
      prev.map(query =>
        query.id === queryId ? { ...query, assigned_to: userId } : query
      )
    );
  };

  const filteredQueries = queries.filter(query => {
    const customerName = (query.customer?.name || '').toLowerCase();
    const matchesSearch = customerName.includes(searchTerm.toLowerCase()) ||
      query.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      query.type.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = typeFilter ? query.type === typeFilter : true;
    const matchesAssignee = assigneeFilter ? query.assignedTo?.id?.toString() === assigneeFilter : true;

    return matchesSearch && matchesType && matchesAssignee;
  });

  const uniqueAssignees = Array.from(
    new Map(
      queries
        .filter(q => q.assignedTo)
        .map(q => [q.assignedTo!.id, q.assignedTo!])
    ).values()
  );

  const normalizeStatus = (status: string) => status.toUpperCase().replace(/-/g, ' ');
  const openItems = filteredQueries.filter(q => normalizeStatus(q.status || '') === 'OPEN');
  const inProgressItems = filteredQueries.filter(q => normalizeStatus(q.status || '') === 'IN PROGRESS');
  const closedItems = filteredQueries.filter(q => normalizeStatus(q.status || '') === 'CLOSED');
  const bookedItems = filteredQueries.filter(q => normalizeStatus(q.status || '') === 'BOOKED');

  const [dragOver, setDragOver] = useState<string | null>(null);
  const [, setDraggingId] = useState<number | null>(null);
  const [lanePositions, setLanePositions] = useState<Record<string, number[]>>({});

  // Initialize and maintain lane order positions without overriding manual reorders
  useEffect(() => {
    const next: Record<string, number[]> = { ...lanePositions };
    const apply = (status: string, arr: Query[]) => {
      const existing = next[status] || [];
      const ids = arr.map(q => q.id);
      // keep existing order for ids that remain
      const kept = existing.filter(id => ids.includes(id));
      // append new ids in current visible order
      const appended = ids.filter(id => !kept.includes(id));
      next[status] = [...kept, ...appended];
    };
    apply('OPEN', openItems);
    apply('IN PROGRESS', inProgressItems);
    apply('CLOSED', closedItems);
    apply('BOOKED', bookedItems);
    setLanePositions(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries, searchTerm, typeFilter, assigneeFilter]);

  const orderLane = (items: Query[], status: string) => {
    const order = lanePositions[status] || [];
    const orderIndex = new Map(order.map((id, idx) => [id, idx] as [number, number]));
    return items.slice().sort((a, b) => {
      const ai = orderIndex.has(a.id) ? (orderIndex.get(a.id) as number) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b.id) ? (orderIndex.get(b.id) as number) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  };

  const openList = orderLane(openItems, 'OPEN');
  const inProgressList = orderLane(inProgressItems, 'IN PROGRESS');
  const closedList = orderLane(closedItems, 'CLOSED');
  const bookedList = orderLane(bookedItems, 'BOOKED');

  const handleDropTo = async (status: 'OPEN' | 'IN PROGRESS' | 'CLOSED' | 'BOOKED', ev: React.DragEvent<HTMLElement>, beforeId?: number | null) => {
    ev.preventDefault();
    setDragOver(null);
    const idStr = ev.dataTransfer.getData('text/plain');
    const id = parseInt(idStr || '0');
    if (!id) return;
    // Reorder / move in lanePositions first (optimistic)
    setLanePositions(prev => {
      const next = { ...prev } as Record<string, number[]>;
      // remove from all lanes
      Object.keys(next).forEach(k => {
        next[k] = (next[k] || []).filter(x => x !== id);
      });
      const lane = status;
      const arr = next[lane] ? [...next[lane]] : [];
      let insertAt = 0;
      if (beforeId && arr.includes(beforeId)) {
        insertAt = arr.indexOf(beforeId);
      }
      arr.splice(insertAt, 0, id);
      next[lane] = arr;
      return next;
    });

    // If status changed, optimistically update item status
    setQueries(prev => prev.map(q => (q.id === id ? { ...q, status } : q)));
    try {
      await updateQueryStatus(id, status);
    } catch (e) {
      // Reload this query list on failure to reflect server truth
      const customerId = parseInt(searchParams.get('customerId') || '0');
      load(customerId || undefined);
    }
  };

  const renderCard = (query: Query, lane: 'OPEN' | 'IN PROGRESS' | 'CLOSED' | 'BOOKED') => (
    <div
      key={query.id}
      className="query-card compact"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(query.id));
        setDraggingId(query.id);
      }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(lane); }}
      onDrop={(e) => handleDropTo(lane, e, query.id)}
    >
      <div className="qc-row">
        <span
          className="qc-name clickable-name"
          onClick={() => openCustomerModal(query.customer?.id)}
        >
          {query.customer?.name || 'Unknown Customer'}
        </span>
      </div>
      <div className="qc-meta">
        <span className="qc-chip">{query.type}</span>
        <span className="qc-chip">{query.location}</span>
        <span className="qc-chip">PAX: {query.pax}</span>
        <span className="qc-chip">{formatDate(query.queryDate)}</span>
        <span className="qc-chip">{query.assignedTo?.name || 'UnAssigned'}</span>
        <span className="qc-chip">Src: {query.source}</span>
        <span className="qc-chip">Eng: {query.engagementLevel}</span>
      </div>
      <div className="qc-actions">
        <button className="icon-btn" title="Add FollowUp" onClick={() => openCreateFollowUpModal(query.id)}>
          <FaPlus /> <span className="label">Add FollowUp</span>
        </button>
        {(() => {
          const st = String(query.status || '').toUpperCase();
          const shareDisabled = st.includes('BOOK') || st.includes('CANCEL');
          const title = shareDisabled ? 'Disabled for Booked/Cancelled queries' : 'Share Quotation';
          return (
            <button className="icon-btn" title={title} onClick={() => openShareQuotationModal(query.id)} disabled={shareDisabled}>
              <FaShare /> <span className="label">Share Quotation</span>
            </button>
          );
        })()}
        <button className="icon-btn" title="View History" onClick={() => viewFollowUps(query.id)}>
          <FaEye /> <span className="label">History</span>
        </button>
        <button className="icon-btn" title={query.assignedTo ? 'ReAssign' : 'Assign'} onClick={() => openAssignmentModal(query.id)}>
          <FaAssign /> <span className="label">Assign</span>
        </button>
      </div>
    </div>
  );

  const customerIdFromParams = parseInt(searchParams.get('customerId') || '0');

  return (
    <Layout>
      <div className="packages-header">
        <h2>Customer Queries</h2>
        <div className="packages-actions">
          <button className="btn" onClick={() => load(customerIdFromParams)} disabled={loading} title="Reload queries">
            <FiRefreshCw /> {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            className="btn"
            onClick={() => {
              if (customerIdFromParams) {
                setNewQueryCustomerId(customerIdFromParams);
                setShowCreateQueryModal(true);
              } else {
                setShowCustomerPicker(true);
              }
            }}
            title={customerIdFromParams ? 'Create new query for selected customer' : 'Pick a customer to create a query'}
          >
            <FaPlus /> Add Query
          </button>
        </div>
      </div>

      <div className="packages-filters">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search by name, location, type..."
        />
        <select onChange={(e) => setTypeFilter(e.target.value)} value={typeFilter} className="filter-select" aria-label="Type">
          <option value="">All Types</option>
          <option value="travel">Travel</option>
          <option value="event">Event</option>
        </select>
        <select onChange={(e) => setAssigneeFilter(e.target.value)} value={assigneeFilter} className="filter-select" aria-label="Assignee">
          <option value="">All Assignees</option>
          {uniqueAssignees.map(user => (
            <option key={user.id} value={user.id}>{user.name}</option>
          ))}
        </select>
        <button className="clear-btn" onClick={() => {
          setSearchTerm('');
          setTypeFilter('');
          setAssigneeFilter('');
          setSearchParams({});
          load();
        }}>Clear</button>
      </div>

      <div className="kanban-board">
        <div
          className={`kanban-column open ${dragOver === 'OPEN' ? 'drop-target' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragOver('OPEN')}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDropTo('OPEN', e)}
        >
          <div className="kanban-column-header">Open ({openItems.length})</div>
          {openList.map(q => renderCard(q, 'OPEN'))}
        </div>
        <div
          className={`kanban-column inprogress ${dragOver === 'IN PROGRESS' ? 'drop-target' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragOver('IN PROGRESS')}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDropTo('IN PROGRESS', e)}
        >
          <div className="kanban-column-header">In Progress ({inProgressItems.length})</div>
          {inProgressList.map(q => renderCard(q, 'IN PROGRESS'))}
        </div>
        <div
          className={`kanban-column booked ${dragOver === 'BOOKED' ? 'drop-target' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragOver('BOOKED')}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDropTo('BOOKED', e)}
        >
          <div className="kanban-column-header">Booked ({bookedItems.length})</div>
          {bookedList.map(q => renderCard(q, 'BOOKED'))}
        </div>
        <div
          className={`kanban-column closed ${dragOver === 'CLOSED' ? 'drop-target' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragOver('CLOSED')}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => handleDropTo('CLOSED', e)}
        >
          <div className="kanban-column-header">Closed ({closedItems.length})</div>
          {closedList.map(q => renderCard(q, 'CLOSED'))}
        </div>
      </div>

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {showCreateFollowUpModal && (
        <CreateFollowUpModal
          queryId={queryId}
          onClose={closeAllModals}
        />
      )}

      {showCreateQueryModal && newQueryCustomerId && (
        <CreateQueryModal
          customerId={newQueryCustomerId}
          onClose={() => setShowCreateQueryModal(false)}
          onQueryCreated={(newId) => {
            setShowCreateQueryModal(false);
            const refId = customerIdFromParams || newQueryCustomerId || 0;
            setNewQueryCustomerId(null);
            load(refId || undefined);
          }}
        />
      )}

      {showCustomerPicker && (
        <CustomerPickerModal
          onClose={() => setShowCustomerPicker(false)}
          onSelect={(c) => {
            setShowCustomerPicker(false);
            setNewQueryCustomerId(c.id!);
            setShowCreateQueryModal(true);
          }}
        />
      )}

      {showShareQuotationModal && (
        <StartQuotationModal
          queryId={queryId}
          onClose={() => setShowShareQuotationModal(false)}
          onCreateFromScratch={() => {
            setShowShareQuotationModal(false);
            navigate('/package/builder', { state: { mode: 'quotation', queryId } });
          }}
          onUsePackage={(pkg) => {
            setShowShareQuotationModal(false);
            navigate('/package/builder', { state: { mode: 'quotation', queryId, packageToCopy: pkg } });
          }}
        />
      )}

      {showQueryHistory && (
        <QueryHistoryModal
          queryId={queryId}
          onClose={() => setShowQueryHistory(false)}
          onLog={() => {
            setShowQueryHistory(false);
            setShowCreateFollowUpModal(true);
          }}
        />
      )}

      {showAssignmentModal && (
        <AssignmentModal
          queryId={queryId}
          users={uniqueAssignees as User[]}
          onClose={() => setShowAssignmentModal(false)}
          onAssign={handleAssign}
        />
      )}
    </Layout>
  );
};

export default QueriesPage;
